<template>
  <div class="channel-settings-form">
    <div ref="sentinel" class="sentinel"></div>
    <div ref="stickyButtons" :class="['sticky-buttons', { 'is-sticky': isSticky }]">
      <div class="left-buttons">
        <start-tour-button :loading-tutorial="loadingTutorial" :flow-id="flowId" />
        <mf-button v-if="isEditingChannel" text label="Remover canal" icon="delete" color="vermelho-d20" @click="removeChannel" />
      </div>
      <mf-action-buttons :class="{ 'my-5': !isSticky, 'my-3': isSticky }" :primary-button="primaryButton" :extra-button="extraButton" />
    </div>
    <component :is="selectedChannelForm" :ref="`${selectedChannel}-component`" :settings-id="settingsId" />
  </div>
</template>

<script>
import EmailForm from './EmailForm/EmailForm.vue'
import PushForm from './PushForm'
import SmsForm from './SmsForm/SmsForm.vue'
import WhatsappForm from './WhatsappForm/WhatsappForm.vue'
import { mapActions } from 'vuex'
import { mariaCampaign } from '@/modules/maria/campanha-da-maria-v2/mixins/maria-campaign'
import channelsDefaultMessages from '@/modules/maria/helpers/maria-default-values'
import StartTourButton from '@/components/tour/StartTourButton.vue'

export default {
  components: {
    EmailForm,
    PushForm,
    SmsForm,
    WhatsappForm,
    StartTourButton,
  },
  mixins: [mariaCampaign],
  props: {
    selectedChannel: {
      type: String,
      required: true,
    },
    settingsId: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isEditingChannel: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isSticky: false,
      mapChannelNames: {
        sms: 'SMS',
        push: 'Push App',
        email: 'E-mail',
        whatsapp: 'WhatsApp',
      },
      loadingTutorial: false,
      flowId: '959f0ac2-20d8-4b96-ba1e-e489925bffe8',
      loading: false,
    }
  },
  emits: ['close', 'channel-saved'],
  computed: {
    selectedChannelForm() {
      const channelForms = {
        email: EmailForm,
        push: PushForm,
        sms: SmsForm,
        whatsapp: WhatsappForm,
      }
      return channelForms[this.selectedChannel] || null
    },
    primaryButton() {
      return {
        text: 'Salvar e voltar',
        color: 'marca-0-l40',
        action: this.handleChannelForm,
        isVisible: true,
        isDisabled: this.isDisabled || this.loading,
        isLoading: this.loading,
      }
    },
    extraButton() {
      return {
        text: 'Cancelar',
        action: this.cancelAction,
        isVisible: true,
        isDisabled: false,
      }
    },
    isDisabled() {
      const formComponent = this.$refs[`${this.selectedChannel}-component`]
      return formComponent?.isFormDisabled || this.disabled
    },
  },
  mounted() {
    this.setupStickyObserver()
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    ...mapActions(['setMessagesDispatcherSettingsChannel']),
    setupStickyObserver() {
      const options = {
        rootMargin: '-64px 0px 0px 0px',
        threshold: [0, 1],
      }

      this.observer = new IntersectionObserver(([entry]) => {
        this.isSticky = !entry.isIntersecting
      }, options)

      this.$nextTick(() => {
        if (this.$refs.sentinel) {
          this.observer.observe(this.$refs.sentinel)
        }
      })
    },
    async handleChannelForm() {
      this.loading = true

      const formComponent = this.$refs[`${this.selectedChannel}-component`]
      let isValid = await formComponent.validInput()

      if (!isValid) {
        this.$snackbar({ message: 'Corrija os erros de preenchimento do formulário.', snackbarColor: 'error' })
        this.loading = false
        return
      }

      const updatedChannel = {
        ...(await formComponent.getChannelData()),
        priority: '',
        active: true,
      }

      try {
        this.setMessagesDispatcherSettingsChannel({
          scheduleSettingsId: this.settingsId,
          channel: this.selectedChannel,
          channelData: updatedChannel,
        })
        this.$emit('close')
        this.$emit('channel-saved')
      } catch (error) {
        console.error('Erro ao salvar as configurações do canal:', error)
        this.$snackbar({
          message: 'Erro ao salvar as configurações do canal.',
          snackbarColor: 'error',
        })
      } finally {
        this.loading = false
      }
    },
    async cancelAction() {
      if (!this.isEditingChannel) {
        this.$emit('close')
        return
      }

      const confirmed = await this.$confirm({
        title: 'Tem certeza?',
        message: 'As alterações realizadas não serão salvas, deseja mesmo voltar para a tela de Agendamento?',
        confirmText: 'Continuar editando',
        cancelText: 'Descartar alterações',
      })
        .then(() => true)
        .catch(() => false)

      if (!confirmed) {
        this.$emit('close')
        this.$emit('channel-saved')
      }
    },
    async removeChannel() {
      const confirmed = await this.$confirm({
        title: 'Remover canal',
        message: `Deseja realmente remover o canal ${
          this.mapChannelNames[this.selectedChannel]
        } desse disparo? As configurações de mensagem cadastradas serão perdidas.`,
        confirmText: 'Remover',
        cancelText: 'Cancelar',
        confirmColor: 'vermelho-d20',
      })
        .then(() => true)
        .catch(() => false)

      if (confirmed) {
        const campaignSettings = this.campaign.messages_dispatcher.settings.schedule_settings.find(
          (schedule_settings) => schedule_settings.id === this.settingsId
        )

        if (!campaignSettings) {
          this.$snackbar({ message: 'Configurações do canal não encontradas.', snackbarColor: 'error' })
          throw Error(`Campaign settings not found for ${this.settingsId}`)
        }

        const updatedChannel = {
          ...campaignSettings.channels[this.selectedChannel],
          message: channelsDefaultMessages[this.selectedChannel],
          active: false,
          priority: '',
        }

        try {
          this.setMessagesDispatcherSettingsChannel({
            scheduleSettingsId: this.settingsId,
            channel: this.selectedChannel,
            channelData: updatedChannel,
          })
          this.$emit('close')
          this.$emit('channel-saved')
        } catch (error) {
          console.error('Erro ao remover o canal:', error)
          this.$snackbar({
            message: 'Erro ao remover o canal.',
            snackbarColor: 'error',
          })
        }
      }
    },
  },
}
</script>

<style lang="scss">
.channel-settings-form {
  position: relative;

  .sentinel {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    visibility: hidden;
  }

  .title {
    font-family: Lato;
    font-weight: 600;
    font-size: 18px !important;
    line-height: 21.6px;
    color: $marca-0-l20;
  }

  .channel-icon {
    color: $marca-0-l20;
    font-size: 20px;
  }

  .clickable-text {
    cursor: pointer;
  }

  .sticky-buttons {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: 64px;
    z-index: 1;
    background-color: #f6f6f8;
    padding: 0 20px;
    margin-left: -12px;
    margin-right: -12px;
    transition: all 0.3s ease;

    &.is-sticky {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      background-color: #ffffff;
      z-index: 3;
    }
  }

  .left-buttons {
    display: flex;
    align-items: baseline;
    gap: 12px;
  }
}
</style>
